import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, User, Heart, ChevronDown, Sun, Moon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';
import { useCartStore } from '@/stores/cart-store';
import { useUIStore } from '@/stores/ui-store';
import { useSettings } from '@/hooks/useProducts';
import { useTheme } from '@/hooks/useTheme';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { IS_RUMIO, IS_LODGING, IS_SINGLE_PROPERTY } from '@/config/site';
import LocaleSwitcher from '@/components/LocaleSwitcher';
import NotificationBell from '@/components/layout/NotificationBell';
import { resolveAppNavIcon } from '@/lib/app-nav-icon';
import { canAccessAdminShell } from '@/lib/roles';
import { adminLinkDest } from './nav-types';
import type { AppNavSection } from './nav-types';

interface HeaderActionsProps {
  visibleAppSections: AppNavSection[];
  showEcommerce: boolean;
  showCourses: boolean;
  /** Class for the icon buttons; variants pass their own colour scheme. */
  iconClass?: string;
  /** Class for the outlined Sign In link (desktop). */
  signInClass?: string;
  /** Extra control rendered before the account menu (a template's pill CTA). */
  children?: ReactNode;
}

const DEFAULT_ICON_CLASS =
  'p-2 text-sage-800 hover:text-sage-900 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600';
const DEFAULT_SIGN_IN_CLASS =
  'hidden sm:inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-sage-800 hover:text-sage-900 border border-sage-600 rounded-lg hover:bg-sage-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600';

/**
 * The actions cluster every header variant shares: locale switcher, theme
 * toggle, search, wishlist, cart, the host-side notification bell, the
 * lodging Book Now CTA, and the account menu (desktop) or Sign In link.
 */
export default function HeaderActions({
  visibleAppSections,
  showEcommerce,
  showCourses,
  iconClass = DEFAULT_ICON_CLASS,
  signInClass = DEFAULT_SIGN_IN_CLASS,
  children,
}: HeaderActionsProps) {
  const { t } = useTranslation(['nav', 'common']);
  const { user, logout } = useAuthStore();
  const itemCount = useCartStore((s) => s.itemCount());
  const { openCartDrawer } = useUIStore();
  const { data: settingsData } = useSettings();
  const cartDrawerEnabled = settingsData?.settings?.upsell_config?.cart_drawer?.enabled;
  // Default ON — existing sites without the setting persisted should keep
  // rendering the icon. Tenant turns it off via admin → Settings → Header.
  const headerSearchEnabled = settingsData?.settings?.header_search_enabled !== false;
  const { theme, toggleTheme } = useTheme();
  const { isEnabled } = useFeatureFlags();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const userMenuButtonRef = useRef<HTMLButtonElement>(null);

  // Close user menu on Escape and return focus to the trigger.
  useEffect(() => {
    if (!userMenuOpen) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setUserMenuOpen(false);
        userMenuButtonRef.current?.focus();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [userMenuOpen]);

  const cartBadge = itemCount > 0 && (
    <span className="absolute -top-0.5 -right-0.5 bg-terra text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center" aria-hidden="true">
      {itemCount > 99 ? '99+' : itemCount}
    </span>
  );

  return (
    <div className="flex items-center gap-1 sm:gap-2">
      <LocaleSwitcher className="hidden sm:inline-flex" />
      <button
        onClick={toggleTheme}
        className={iconClass}
        aria-label={theme === 'light' ? t('common:accessibility.darkTheme') : t('common:accessibility.lightTheme')}
      >
        {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
      </button>

      {headerSearchEnabled && isEnabled('meilisearch') && (
        <Link to="/search" className={iconClass} aria-label={t('nav:search')}>
          <Search className="h-5 w-5" />
        </Link>
      )}

      {showEcommerce && user && (
        <Link
          to="/account/wishlist"
          className={cn(iconClass, 'hidden sm:flex')}
          aria-label={t('nav:wishlist')}
        >
          <Heart className="h-5 w-5" />
        </Link>
      )}

      {showEcommerce && (cartDrawerEnabled ? (
        <button
          onClick={openCartDrawer}
          className={cn('relative', iconClass)}
          aria-label={t('nav:cartWithCount', { count: itemCount })}
        >
          <ShoppingCart className="h-5 w-5" />
          {cartBadge}
        </button>
      ) : (
        <Link
          to="/cart"
          className={cn('relative', iconClass)}
          aria-label={t('nav:cartWithCount', { count: itemCount })}
        >
          <ShoppingCart className="h-5 w-5" />
          {cartBadge}
        </Link>
      ))}

      {/* Host-side notification bell — aggregates `rumio:notifications`
          published by every embedded app (contract v1.10). Hides itself
          when no app has anything to show, so we don't render a
          permanently empty bell. Gated to `user` because anonymous
          visitors don't mount any apps that could publish. */}
      {user && <NotificationBell />}

      {/* Prominent, always-visible booking CTA for lodging tenants.
          Single-property sites jump straight to the on-page booking
          widget (#book); multi-property sites go to the stays list to
          pick a property first. */}
      {IS_LODGING && (
        <Link
          to={IS_SINGLE_PROPERTY ? '/#book' : '/properties'}
          className="inline-flex items-center px-4 py-1.5 bg-cta text-cta-fg font-semibold text-sm rounded-full hover:bg-cta-deep transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cta"
        >
          {t('common:bookNow', { defaultValue: 'Book Now' })}
        </Link>
      )}

      {children}

      {/* User menu — desktop only. The mobile drawer (hamburger,
          left) includes the same account links via PR #169, so
          rendering this avatar dropdown below `lg` produced two
          parallel nav surfaces. Hide it at the same breakpoint
          where the hamburger appears (<lg). */}
      {user ? (
        <div className="relative hidden lg:block" ref={userMenuRef}>
          <button
            ref={userMenuButtonRef}
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className={cn('flex items-center gap-1', iconClass)}
            aria-expanded={userMenuOpen}
            aria-haspopup="true"
            aria-label={t('nav:accountMenu')}
          >
            <User className="h-5 w-5" />
            <ChevronDown className="h-3 w-3 hidden sm:block" />
          </button>

          {userMenuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} />
              <div className="absolute right-0 mt-1 w-48 bg-surface rounded-lg shadow-lg border border-sage-200 z-20 py-1" role="menu">
                <div className="px-4 py-2 text-sm text-sage-500 border-b border-sage-100">
                  {user.firstName || user.email}
                </div>
                <Link to="/account" className="block px-4 py-2 text-sm text-sage-700 hover:bg-sage-50 focus-visible:bg-sage-50 focus-visible:outline-none" role="menuitem" onClick={() => setUserMenuOpen(false)}>{t('nav:myAccount')}</Link>
                {showEcommerce && (
                  <Link to="/account/orders" className="block px-4 py-2 text-sm text-sage-700 hover:bg-sage-50 focus-visible:bg-sage-50 focus-visible:outline-none" role="menuitem" onClick={() => setUserMenuOpen(false)}>{t('nav:orders')}</Link>
                )}
                {showCourses && (
                  <Link to="/account/courses" className="block px-4 py-2 text-sm text-sage-700 hover:bg-sage-50 focus-visible:bg-sage-50 focus-visible:outline-none" role="menuitem" onClick={() => setUserMenuOpen(false)}>{t('nav:myCourses')}</Link>
                )}
                {canAccessAdminShell(user.role) && (
                  <Link to={adminLinkDest(user.role)} className="block px-4 py-2 text-sm text-sage-600 font-medium hover:bg-sage-50 focus-visible:bg-sage-50 focus-visible:outline-none" role="menuitem" onClick={() => setUserMenuOpen(false)}>{t('nav:adminPanel')}</Link>
                )}
                {/* Platform-only: let an existing customer spin up an
                    additional site without hunting for the marketing
                    CTA. Routes into the Site Builder onboarding flow. */}
                {IS_RUMIO && (
                  <Link to="/onboarding" className="block px-4 py-2 text-sm text-sage-600 font-medium hover:bg-sage-50 focus-visible:bg-sage-50 focus-visible:outline-none" role="menuitem" onClick={() => setUserMenuOpen(false)}>{t('nav:createNewSite')}</Link>
                )}
                {visibleAppSections.map((section) => (
                  <div key={section.appId}>
                    <div className="border-t border-sage-100 my-1" aria-hidden />
                    <div className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-wide text-sage-500">
                      {section.appName}
                    </div>
                    {section.items.map((item) => {
                      if (!item.href) return null;
                      const Icon = resolveAppNavIcon(item.icon);
                      return (
                        <Link
                          key={item.href}
                          to={item.href}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-sage-700 hover:bg-sage-50 focus-visible:bg-sage-50 focus-visible:outline-none"
                          role="menuitem"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          {Icon && <Icon className="h-4 w-4" aria-hidden />}
                          <span>{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                ))}
                {visibleAppSections.length > 0 && (
                  <div className="border-t border-sage-100 my-1" aria-hidden />
                )}
                <button
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 focus-visible:bg-red-50 focus-visible:outline-none"
                  role="menuitem"
                  onClick={() => { logout(); setUserMenuOpen(false); navigate('/'); }}
                >
                  {t('nav:signOut')}
                </button>
              </div>
            </>
          )}
        </div>
      ) : (
        <Link to="/login" className={signInClass}>
          {t('nav:signIn')}
        </Link>
      )}
    </div>
  );
}
